/**
 * Hook pour appliquer les changements de scénario aux entités via les mutations GraphQL
 */

import { useCallback, useRef, useEffect } from 'react';
import { useStakeholderController } from '../../hooks/useStakeholderController';
import { useStakeController } from '../../hooks/useStakeController';
import { useRiskController } from '../../hooks/useRiskController';
import { useIndicatorController } from '../../hooks/useIndicatorController';
import { useFishboneController } from '../../hooks/useFishboneController';
import {
	calculateOptimalStakeholderPosition,
	StakeholderSlice,
	StakeholderPolarity
} from '../../utils/stakeholderPlacementOptimizer';
import {
	ElementModification,
	StakeholderChanges,
	IndicatorChanges,
	RiskChanges,
	ConstraintChanges,
	FishboneBranchChanges,
	FishboneItemChanges,
	NewStakeholder,
	NewStake,
	NewRisk,
	NewIndicator,
	NewFishboneBranch,
	NewFishboneItem
} from './types';

export interface ApplyChangesContext {
	projectId: string;
	scenario: any;
	stakeholders: any[];
	stakes: any[];
	risks: any[];
	indicators: any[];
	scenarios: any[];
	fishboneBranches?: any[];
	fishboneItems?: any[];
	// Échelles du projet pour normaliser les valeurs
	likelihoods?: any[];
	impacts?: any[];
}

export interface ApplyResult {
	applied: number;
	errors: string[];
}

// Limite maximale pour éviter l'accumulation excessive
const MAX_PLACED_STAKEHOLDERS = 100;

/**
 * Hook pour appliquer les changements de scénario
 */
export function useApplyScenarioChanges() {
	const { updateStakeholder, createStakeholder } = useStakeholderController();
	const { updateStake, createStake } = useStakeController();
	const { updateRisk, createRisk } = useRiskController();
	const { updateIndicator, createIndicator } = useIndicatorController();
	const {
		createFishboneBranch,
		updateFishboneBranch,
		createFishboneItem,
		updateFishboneItem
	} = useFishboneController();

	// Référence pour tracker les stakeholders déjà placés dans cette session
	const placedStakeholdersRef = useRef<
		Array<{ id: string; x: number; y: number }>
	>([]);

	// Nettoyage automatique au démontage du hook
	useEffect(() => {
		return () => {
			placedStakeholdersRef.current = [];
		};
	}, []);

	/**
	 * Applique les modifications des stakeholders
	 * Calcule les positions optimales côté front pour éviter les collisions
	 */
	const applyStakeholderChanges = useCallback(
		async (
			modifications: ElementModification<StakeholderChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Créer une liste de stakeholders "virtuels" incluant ceux déjà placés
			// pour éviter les collisions
			let virtualStakeholders = [...context.stakeholders];

			// Ajouter les stakeholders placés précédemment dans cette session
			for (const placed of placedStakeholdersRef.current) {
				const existingIndex = virtualStakeholders.findIndex(
					(s) => s.id === placed.id
				);
				if (existingIndex >= 0) {
					// Mettre à jour la position
					virtualStakeholders[existingIndex] = {
						...virtualStakeholders[existingIndex],
						x: placed.x,
						y: placed.y
					};
				}
			}

			for (const mod of modifications) {
				try {
					const stakeholder = context.stakeholders.find(
						(s) => s.id === mod.id
					);
					if (!stakeholder) {
						console.warn(
							`⚠️ Stakeholder ${mod.id} non trouvé, ignoré`
						);
						continue;
					}

					// Préparer les changements
					const changes: any = { ...mod.changes };

					// Si slice ou polarity sont présents, calculer la position optimale
					if (changes.slice || changes.polarity) {
						const slice: StakeholderSlice =
							changes.slice || 'medium';
						const polarity: StakeholderPolarity =
							changes.polarity || 'positive';

						// Exclure le stakeholder actuel de la liste pour le calcul
						const othersForCalculation = virtualStakeholders.filter(
							(s) => s.id !== mod.id
						);

						// Calculer la position optimale
						const optimalPosition =
							calculateOptimalStakeholderPosition(
								othersForCalculation,
								slice,
								polarity
							);

						changes.x = optimalPosition.x;
						changes.y = optimalPosition.y;

				// Log condensé pour éviter la surcharge
				if (process.env.NODE_ENV === 'development') {
					console.log(`📍 Position: "${stakeholder.name}" → (${changes.x}, ${changes.y})`);
				}

						// Mettre à jour la liste virtuelle pour les prochains calculs
						const stakeholderIndex = virtualStakeholders.findIndex(
							(s) => s.id === mod.id
						);
						if (stakeholderIndex >= 0) {
							virtualStakeholders[stakeholderIndex] = {
								...virtualStakeholders[stakeholderIndex],
								x: changes.x,
								y: changes.y
							};
						}

					// Sauvegarder pour les prochains appels (éviter les duplicats)
					const existingIdx = placedStakeholdersRef.current.findIndex(p => p.id === mod.id);
					if (existingIdx >= 0) {
						placedStakeholdersRef.current[existingIdx] = { id: mod.id, x: changes.x, y: changes.y };
					} else {
						placedStakeholdersRef.current.push({ id: mod.id, x: changes.x, y: changes.y });
					}
					
					// Limiter la taille pour éviter les fuites mémoire
					if (placedStakeholdersRef.current.length > MAX_PLACED_STAKEHOLDERS) {
						placedStakeholdersRef.current = placedStakeholdersRef.current.slice(-MAX_PLACED_STAKEHOLDERS);
					}
				}

					// Supprimer slice/polarity des changements (non nécessaires pour la mutation)
					delete changes.slice;
					delete changes.polarity;

				if (process.env.NODE_ENV === 'development') {
					console.log(`🔄 Mise à jour stakeholder "${stakeholder.name}"`);
				}

					await updateStakeholder(
						mod.id,
						context.stakeholders,
						context.scenario,
						changes
					);
				result.applied++;
				} catch (error) {
					const errorMsg =
						error instanceof Error
							? error.message
							: 'Erreur inconnue';
					result.errors.push(
						`Erreur stakeholder ${mod.id}: ${errorMsg}`
					);
					console.error(`❌ Erreur stakeholder ${mod.id}:`, error);
				}
			}

			return result;
		},
		[updateStakeholder]
	);

	/**
	 * Applique les modifications des indicateurs
	 */
	const applyIndicatorChanges = useCallback(
		async (
			modifications: ElementModification<IndicatorChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Parallélisé : aucune dépendance d'ordre entre indicateurs.
			await Promise.all(modifications.map(async (mod) => {
				try {
					const stake = context.stakes.find(
						(i) => i.id === mod.id
					);
					if (!stake) {
						console.warn(
							`⚠️ Stake ${mod.id} non trouvé, ignoré`
						);
						return;
					}
					await updateStake(
						mod.id,
						context.stakes,
						context.scenario,
						mod.changes
					);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur stake ${mod.id}: ${errorMsg}`);
					console.error(`❌ Erreur stake ${mod.id}:`, error);
				}
			}));

			return result;
		},
		[updateStake]
	);

	/**
	 * Normalise une valeur 0-100 vers l'échelle du projet
	 */
	const normalizeToScale = (value: number, scaleLength: number): number => {
		if (scaleLength <= 0) return 1;
		// Convertir 0-100 vers 1-scaleLength
		const normalized = Math.round((value / 100) * (scaleLength - 1)) + 1;
		return Math.max(1, Math.min(scaleLength, normalized));
	};

	/**
	 * Applique les modifications des risques
	 */
	const applyRiskChanges = useCallback(
		async (
			modifications: ElementModification<RiskChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Récupérer les échelles du projet
			const maxLikelihood = context.likelihoods?.length || 5;
			const maxImpact = context.impacts?.length || 5;

			await Promise.all(modifications.map(async (mod) => {
				try {
					const risk = context.risks.find((r) => r.id === mod.id);
					if (!risk) {
						console.warn(`⚠️ Risk ${mod.id} non trouvé, ignoré`);
						return;
					}
					const normalizedChanges: any = { ...mod.changes };
					if (normalizedChanges.likelihood !== undefined) {
						normalizedChanges.likelihood = normalizeToScale(
							normalizedChanges.likelihood,
							maxLikelihood
						);
					}
					if (normalizedChanges.impact !== undefined) {
						normalizedChanges.impact = normalizeToScale(
							normalizedChanges.impact,
							maxImpact
						);
					}
					await updateRisk(
						mod.id,
						context.risks,
						context.scenario,
						normalizedChanges
					);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur risk ${mod.id}: ${errorMsg}`);
					console.error(`❌ Erreur risk ${mod.id}:`, error);
				}
			}));

			return result;
		},
		[updateRisk]
	);

	/**
	 * Applique les modifications des contraintes
	 */
	const applyConstraintChanges = useCallback(
		async (
			modifications: ElementModification<ConstraintChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			await Promise.all(modifications.map(async (mod) => {
				try {
					const indicator = context.indicators.find(
						(c) => c.id === mod.id
					);
					if (!indicator) {
						console.warn(`⚠️ Indicator ${mod.id} non trouvé, ignoré`);
						return;
					}
					await updateIndicator(
						mod.id,
						context.indicators,
						context.scenario,
						mod.changes
					);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur indicator ${mod.id}: ${errorMsg}`);
					console.error(`❌ Erreur indicator ${mod.id}:`, error);
				}
			}));

			return result;
		},
		[updateIndicator]
	);

	/**
	 * Réinitialise le tracking des positions placées
	 */
	const resetPlacedPositions = useCallback(() => {
		placedStakeholdersRef.current = [];
	}, []);

	/**
	 * Vérifie si un élément avec le même nom existe déjà (case-insensitive)
	 */
	const elementExists = (name: string, existingItems: any[]): boolean => {
		const normalizedName = name?.toLowerCase().trim();
		return existingItems.some(
			(item) => item.name?.toLowerCase().trim() === normalizedName
		);
	};

	/**
	 * Crée de nouveaux stakeholders à partir des données de l'IA
	 */
	const createNewStakeholders = useCallback(
		async (
			newElements: NewStakeholder[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Créer une liste de stakeholders "virtuels" pour le calcul de position
			let virtualStakeholders = [...context.stakeholders];

			// Ajouter les stakeholders placés précédemment
			for (const placed of placedStakeholdersRef.current) {
				const existingIndex = virtualStakeholders.findIndex(
					(s) => s.id === placed.id
				);
				if (existingIndex >= 0) {
					virtualStakeholders[existingIndex] = {
						...virtualStakeholders[existingIndex],
						x: placed.x,
						y: placed.y
					};
				}
			}

			// Filtrer les doublons (noms déjà existants)
			const seenNames = new Set<string>();
			const filteredElements = newElements.filter((element) => {
				const normalizedName = element.name?.toLowerCase().trim();
				
				// Vérifier si existe déjà dans le projet
				if (elementExists(element.name, context.stakeholders)) {
					console.log(`⚠️ Stakeholder ignoré (existe déjà): "${element.name}"`);
					return false;
				}
				
				// Vérifier si déjà vu dans cette liste
				if (seenNames.has(normalizedName)) {
					console.log(`⚠️ Stakeholder doublon ignoré: "${element.name}"`);
					return false;
				}
				
				seenNames.add(normalizedName);
				return true;
			});

			for (const element of filteredElements) {
				try {
					// Calculer la position optimale
					const slice: StakeholderSlice = element.slice || 'medium';
					const polarity: StakeholderPolarity =
						element.polarity || 'positive';

					const optimalPosition = calculateOptimalStakeholderPosition(
						virtualStakeholders,
						slice,
						polarity
					);


					const values = {
						name: element.name,
						description:
							element.description || element.reason || '',
						x: optimalPosition.x,
						y: optimalPosition.y,
						enabled: true
					};

					const createResult = await createStakeholder(
						context.projectId,
						context.stakeholders,
						values,
						{},
						context.scenario,
						context.scenarios
					);

					if (createResult.data?.createStakeholder?.id) {
						const newId = createResult.data.createStakeholder.id;

						// Mettre à jour la liste virtuelle
						virtualStakeholders.push({
							id: newId,
							name: element.name,
							x: optimalPosition.x,
							y: optimalPosition.y
						});

						// Sauvegarder pour les prochains appels (éviter les duplicats)
						const existingIdx = placedStakeholdersRef.current.findIndex(p => p.id === newId);
						if (existingIdx >= 0) {
							placedStakeholdersRef.current[existingIdx] = { id: newId, x: optimalPosition.x, y: optimalPosition.y };
						} else {
							placedStakeholdersRef.current.push({ id: newId, x: optimalPosition.x, y: optimalPosition.y });
						}
						
						// Limiter la taille pour éviter les fuites mémoire
						if (placedStakeholdersRef.current.length > MAX_PLACED_STAKEHOLDERS) {
							placedStakeholdersRef.current = placedStakeholdersRef.current.slice(-MAX_PLACED_STAKEHOLDERS);
						}

						result.applied++;
					}
				} catch (error) {
					const errorMsg =
						error instanceof Error
							? error.message
							: 'Erreur inconnue';
					result.errors.push(
						`Erreur création stakeholder ${element.name}: ${errorMsg}`
					);
					console.error(
						`❌ Erreur création stakeholder ${element.name}:`,
						error
					);
				}
			}

			return result;
		},
		[createStakeholder]
	);

	/**
	 * Crée de nouveaux indicateurs à partir des données de l'IA
	 */
	const createNewStakes = useCallback(
		async (
			newElements: NewStake[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Filtrer les doublons (noms déjà existants)
			const seenNames = new Set<string>();
			const filteredElements = newElements.filter((element) => {
				const normalizedName = element.name?.toLowerCase().trim();
				
				if (elementExists(element.name, context.stakes)) {
					console.log(`⚠️ Indicateur ignoré (existe déjà): "${element.name}"`);
					return false;
				}
				
				if (seenNames.has(normalizedName)) {
					console.log(`⚠️ Indicateur doublon ignoré: "${element.name}"`);
					return false;
				}
				
				seenNames.add(normalizedName);
				return true;
			});

			await Promise.all(filteredElements.map(async (element) => {
				try {
					const values = {
						name: element.name,
						description: element.description || element.reason || '',
						trend: element.trend?.toString() || '50',
						weight: element.weight || 1,
						enabled: true
					};
					const createResult = await createStake(
						context.projectId,
						values,
						{},
						context.scenario,
						context.scenarios
					);
					if (createResult.data?.createStake?.id) {
						result.applied++;
					}
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur création indicateur ${element.name}: ${errorMsg}`);
					console.error(`❌ Erreur création indicateur ${element.name}:`, error);
				}
			}));

			return result;
		},
		[createStake]
	);

	/**
	 * Normalise une valeur 0-100 vers l'échelle du projet (pour création)
	 */
	const normalizeToScaleForCreate = (
		value: number,
		scaleLength: number
	): number => {
		if (scaleLength <= 0) return 1;
		// Convertir 0-100 vers 1-scaleLength
		const normalized = Math.round((value / 100) * (scaleLength - 1)) + 1;
		return Math.max(1, Math.min(scaleLength, normalized));
	};

	/**
	 * Crée de nouveaux risques/opportunités à partir des données de l'IA
	 */
	const createNewRisks = useCallback(
		async (
			newElements: NewRisk[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Récupérer les échelles du projet
			const maxLikelihood = context.likelihoods?.length || 5;
			const maxImpact = context.impacts?.length || 5;

			// Filtrer les doublons (noms déjà existants)
			const seenNames = new Set<string>();
			const filteredElements = newElements.filter((element) => {
				const normalizedName = element.name?.toLowerCase().trim();
				
				if (elementExists(element.name, context.risks)) {
					console.log(`⚠️ Risque/Opportunité ignoré (existe déjà): "${element.name}"`);
					return false;
				}
				
				if (seenNames.has(normalizedName)) {
					console.log(`⚠️ Risque/Opportunité doublon ignoré: "${element.name}"`);
					return false;
				}
				
				seenNames.add(normalizedName);
				return true;
			});

			await Promise.all(filteredElements.map(async (element) => {
				try {
					const normalizedLikelihood = normalizeToScaleForCreate(
						element.likelihood || 50,
						maxLikelihood
					);
					const normalizedImpact = normalizeToScaleForCreate(
						element.impact || 50,
						maxImpact
					);
					const values = {
						name: element.name,
						description: element.description || element.reason || '',
						likelihood: normalizedLikelihood,
						impact: normalizedImpact,
						isOpportunity: element.isOpportunity || false,
						enabled: true
					};
					const createResult = await createRisk(
						context.projectId,
						context.risks,
						values,
						context.scenario,
						{},
						context.scenarios
					);
					if (createResult.data?.createRisk?.id) {
						result.applied++;
					}
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur création ${element.isOpportunity ? 'opportunité' : 'risque'} ${element.name}: ${errorMsg}`);
					console.error(`❌ Erreur création ${element.isOpportunity ? 'opportunité' : 'risque'} ${element.name}:`, error);
				}
			}));

			return result;
		},
		[createRisk]
	);

	/**
	 * Crée de nouvelles contraintes à partir des données de l'IA
	 */
	const createNewIndicators = useCallback(
		async (
			newElements: NewIndicator[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };

			// Filtrer les doublons (noms déjà existants)
			const seenNames = new Set<string>();
			const filteredElements = newElements.filter((element) => {
				const normalizedName = element.name?.toLowerCase().trim();
				
				if (elementExists(element.name, context.indicators)) {
					console.log(`⚠️ Contrainte ignorée (existe déjà): "${element.name}"`);
					return false;
				}
				
				if (seenNames.has(normalizedName)) {
					console.log(`⚠️ Contrainte doublon ignorée: "${element.name}"`);
					return false;
				}
				
				seenNames.add(normalizedName);
				return true;
			});

			await Promise.all(filteredElements.map(async (element) => {
				try {
					const values = {
						name: element.name,
						description: element.description || element.reason || '',
						trend: element.trend?.toString() || '50',
						weight: 1,
						enabled: true
					};
					const createResult = await createIndicator(
						context.projectId,
						values,
						{},
						context.scenario,
						context.scenarios
					);
					if (createResult.data?.createIndicator?.id) {
						result.applied++;
					}
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur création contrainte ${element.name}: ${errorMsg}`);
					console.error(`❌ Erreur création contrainte ${element.name}:`, error);
				}
			}));

			return result;
		},
		[createIndicator]
	);

	/**
	 * Applique les modifications des branches fishbone (override scenario via
	 * useFishboneController qui route automatiquement vers updateFishboneBranchScenario
	 * quand `scenario` est non-null).
	 */
	const applyFishboneBranchChanges = useCallback(
		async (
			modifications: ElementModification<FishboneBranchChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };
			const branches = context.fishboneBranches || [];

			await Promise.all(modifications.map(async (mod) => {
				try {
					const branch = branches.find((b) => b.id === mod.id);
					if (!branch) {
						console.warn(`⚠️ FishboneBranch ${mod.id} non trouvé, ignoré`);
						return;
					}
					await updateFishboneBranch(mod.id, branches, context.scenario, mod.changes);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur fishbone branch ${mod.id}: ${errorMsg}`);
					console.error(`❌ Erreur fishbone branch ${mod.id}:`, error);
				}
			}));

			return result;
		},
		[updateFishboneBranch]
	);

	const applyFishboneItemChanges = useCallback(
		async (
			modifications: ElementModification<FishboneItemChanges>[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };
			const items = context.fishboneItems || [];

			await Promise.all(modifications.map(async (mod) => {
				try {
					const item = items.find((i) => i.id === mod.id);
					if (!item) {
						console.warn(`⚠️ FishboneItem ${mod.id} non trouvé, ignoré`);
						return;
					}
					const normalized: FishboneItemChanges = { ...mod.changes };
					if (typeof normalized.trend === 'number') {
						normalized.trend = String(normalized.trend) as any;
					}
					await updateFishboneItem(mod.id, items, context.scenario, normalized);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur fishbone item ${mod.id}: ${errorMsg}`);
					console.error(`❌ Erreur fishbone item ${mod.id}:`, error);
				}
			}));

			return result;
		},
		[updateFishboneItem]
	);

	/**
	 * Crée de nouvelles branches fishbone proposées par l'IA. Les items inline
	 * (branch.items) sont créés ensuite, attachés à la branche fraîchement créée.
	 */
	const createNewFishboneBranches = useCallback(
		async (
			newElements: NewFishboneBranch[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };
			const existingBranches = context.fishboneBranches || [];

			// Filtrer les doublons
			const seenNames = new Set<string>();
			const filtered = newElements.filter((el) => {
				const norm = el.name?.toLowerCase().trim();
				if (!norm) return false;
				if (elementExists(el.name, existingBranches)) {
					console.log(`⚠️ Branche fishbone ignorée (existe): "${el.name}"`);
					return false;
				}
				if (seenNames.has(norm)) {
					console.log(`⚠️ Branche fishbone doublon: "${el.name}"`);
					return false;
				}
				seenNames.add(norm);
				return true;
			});

			// Sequential branch creation (need ids before items). Items per branch
			// in parallel.
			for (const branch of filtered) {
				try {
					const branchValues = {
						name: branch.name,
						description: branch.description || branch.reason || '',
						weight: branch.weight ?? 5,
						enabled: true
					};
					const createResult: any = await createFishboneBranch(
						context.projectId,
						branchValues,
						context.scenario,
						context.scenarios
					);
					const newBranchId = createResult?.data?.createFishboneBranch?.id;
					if (!newBranchId) {
						result.errors.push(`Création branche fishbone ${branch.name}: pas d'id retourné`);
						continue;
					}
					result.applied++;

					if (Array.isArray(branch.items) && branch.items.length > 0) {
						const itemPromises = branch.items.map(async (item) => {
							try {
								const itemValues = {
									name: item.name,
									description: item.description || item.reason || '',
									trend: item.trend != null ? String(item.trend) : '50',
									completion: item.completion ?? 0,
									weight: item.weight ?? 1,
									enabled: true
								};
								await createFishboneItem(newBranchId, itemValues, {}, context.scenario, context.scenarios);
								result.applied++;
							} catch (error) {
								const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
								result.errors.push(`Erreur création item fishbone ${item.name}: ${errorMsg}`);
								console.error(`❌ Erreur création item fishbone ${item.name}:`, error);
							}
						});
						await Promise.all(itemPromises);
					}
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur création branche fishbone ${branch.name}: ${errorMsg}`);
					console.error(`❌ Erreur création branche fishbone ${branch.name}:`, error);
				}
			}

			return result;
		},
		[createFishboneBranch, createFishboneItem]
	);

	/**
	 * Crée de nouveaux items fishbone à attacher à des branches existantes.
	 */
	const createNewFishboneItems = useCallback(
		async (
			newElements: NewFishboneItem[],
			context: ApplyChangesContext
		): Promise<ApplyResult> => {
			const result: ApplyResult = { applied: 0, errors: [] };
			const branches = context.fishboneBranches || [];
			const items = context.fishboneItems || [];

			const resolveBranchId = (el: NewFishboneItem): string | undefined => {
				if (el.branchId) {
					const direct = branches.find((b) => b.id === el.branchId);
					if (direct) return direct.id;
				}
				if (el.branchName) {
					const norm = el.branchName.toLowerCase().trim();
					const match = branches.find((b) => b.name?.toLowerCase().trim() === norm);
					if (match) return match.id;
				}
				return undefined;
			};

			const seenNames = new Set<string>();
			const filtered = newElements.filter((el) => {
				const norm = el.name?.toLowerCase().trim();
				if (!norm) return false;
				if (elementExists(el.name, items)) {
					console.log(`⚠️ Item fishbone ignoré (existe): "${el.name}"`);
					return false;
				}
				if (seenNames.has(norm)) {
					console.log(`⚠️ Item fishbone doublon: "${el.name}"`);
					return false;
				}
				seenNames.add(norm);
				return true;
			});

			await Promise.all(filtered.map(async (el) => {
				try {
					const branchId = resolveBranchId(el);
					if (!branchId) {
						result.errors.push(`Item fishbone ${el.name}: branche introuvable`);
						return;
					}
					const values = {
						name: el.name,
						description: el.description || el.reason || '',
						trend: el.trend != null ? String(el.trend) : '50',
						completion: el.completion ?? 0,
						weight: el.weight ?? 1,
						enabled: true
					};
					await createFishboneItem(branchId, values, {}, context.scenario, context.scenarios);
					result.applied++;
				} catch (error) {
					const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
					result.errors.push(`Erreur création item fishbone ${el.name}: ${errorMsg}`);
					console.error(`❌ Erreur création item fishbone ${el.name}:`, error);
				}
			}));

			return result;
		},
		[createFishboneItem]
	);

	return {
		applyStakeholderChanges,
		applyIndicatorChanges,
		applyRiskChanges,
		applyConstraintChanges,
		applyFishboneBranchChanges,
		applyFishboneItemChanges,
		createNewStakeholders,
		createNewStakes,
		createNewRisks,
		createNewIndicators,
		createNewFishboneBranches,
		createNewFishboneItems,
		resetPlacedPositions
	};
}
